import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { UserContext } from "../../../../../../../App";
import "./index.css";

const CATEGORY_COLORS = {
  "Pallet Apps":         "#1662E8",
  "Accounting":          "#16A34A",
  "Aggregators":         "#7C3AED",
  "Billing":             "#0EA5E9",
  "Logistics":           "#F97316",
  "Marketing":           "#EC4899",
  "Property Management": "#6B7280",
  "Sales Channel":       "#D97706",
};
const getCatColor = (cat) => CATEGORY_COLORS[cat] || "#1662E8";

function inrFormat(amount) {
  if (amount == null) return "—";
  const n = Number(amount);
  if (isNaN(n)) return "—";
  if (n === 0)  return "₹0";
  if (n >= 10_000_000) return `₹${(n / 10_000_000).toFixed(1).replace(/\.0$/, "")}Cr`;
  if (n >= 100_000)    return `₹${(n / 100_000).toFixed(1).replace(/\.0$/, "")}L`;
  return `₹${n.toLocaleString("en-IN")}`;
}

function getMonogram(name) {
  if (!name) return "??";
  const words = name.trim().split(/\s+/);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

function HeroLogo({ addon, catColor }) {
  const [failed, setFailed] = useState(false);
  if (addon.logoUrl && !failed) {
    return (
      <img
        className="ad-hero-logo"
        src={addon.logoUrl}
        alt={addon.packageName}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <div className="ad-hero-monogram" style={{ background: catColor }}>
      {getMonogram(addon.packageName)}
    </div>
  );
}

function StatusBadge({ addon }) {
  if (addon.implementationStatus && addon.status === "ACTIVE") {
    return <span className="ad-badge ad-badge-green">✓ Connected</span>;
  }
  if (addon.status !== "ACTIVE") {
    return <span className="ad-badge ad-badge-amber">Inactive</span>;
  }
  return <span className="ad-badge ad-badge-gray">Available</span>;
}

function PriceBadge({ addon }) {
  if (!addon.chargeable) {
    return <span className="ad-badge ad-badge-free">Free</span>;
  }
  const opts    = addon.pricingOptions || [];
  const monthly = opts.find((o) => o.billingCycle === "MONTHLY" && o.status === "ACTIVE");
  const annual  = opts.find((o) => o.billingCycle === "ANNUAL"  && o.status === "ACTIVE");
  const chosen  = monthly || annual;
  if (chosen) {
    return (
      <span className="ad-badge ad-badge-blue">
        {inrFormat(chosen.netPrice)}/{chosen.billingCycle === "MONTHLY" ? "mo" : "yr"}
      </span>
    );
  }
  return <span className="ad-badge ad-badge-blue">Paid</span>;
}

export default function AppDetail() {
  const navigate   = useNavigate();
  const location   = useLocation();
  const { color }  = useContext(UserContext);
  const isDark     = color === "dark";

  const addon    = location.state?.addon;
  const catColor = getCatColor(addon?.category);

  if (!addon) {
    return (
      <div className={`ad-container${isDark ? " dark" : ""}`}>
        <div className="ad-header">
          <button className="ad-back" onClick={() => navigate(-1)} aria-label="Go back">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
          <p className="ad-header-title">App Details</p>
        </div>
        <div style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>
          No app data available.
        </div>
      </div>
    );
  }

  const activePricingOptions = (addon.pricingOptions || []).filter(
    (o) => o.status === "ACTIVE"
  );

  return (
    <div className={`ad-container${isDark ? " dark" : ""}`}>

      {/* Header */}
      <div className="ad-header">
        <button className="ad-back" onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7" />
          </svg>
        </button>
        <p className="ad-header-title">App Details</p>
      </div>

      {/* Body */}
      <div className="ad-body">

        {/* Hero card */}
        <div className="ad-hero">
          <HeroLogo addon={addon} catColor={catColor} />
          <div className="ad-hero-info">
            <p className="ad-hero-name">{addon.packageName}</p>
            <p className="ad-hero-id">{addon.addonId}</p>
            <div className="ad-hero-badges">
              <StatusBadge addon={addon} />
              <PriceBadge  addon={addon} />
              {addon.category && (
                <span
                  className="ad-badge"
                  style={{ background: catColor + "18", color: catColor }}
                >
                  {addon.category}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Description */}
        {(addon.description || addon.shortDescription) && (
          <div className="ad-card">
            <p className="ad-card-title">About</p>
            <p className="ad-desc-text">
              {addon.description || addon.shortDescription}
            </p>
          </div>
        )}

        {/* Pricing */}
        {activePricingOptions.length > 0 && (
          <div className="ad-card">
            <p className="ad-card-title">Pricing</p>
            {activePricingOptions.map((opt) => {
              const gstLabel = opt.gstRate
                ? `GST ${(opt.gstRate * 100).toFixed(0)}%`
                : null;
              return (
                <div className="ad-pricing-row" key={opt.billingCycle + opt.addonId}>
                  <span className="ad-pricing-cycle">
                    {opt.billingCycle === "MONTHLY" ? "Monthly" : "Annual"}
                  </span>
                  <div className="ad-pricing-right">
                    {gstLabel && <span className="ad-pricing-gst">{gstLabel}</span>}
                    <span className="ad-pricing-price">{inrFormat(opt.netPrice)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Details */}
        <div className="ad-card">
          <p className="ad-card-title">Details</p>
          <div className="ad-detail-row">
            <span className="ad-detail-label">Plan category</span>
            <span className="ad-detail-value">{addon.planCategory || "—"}</span>
          </div>
          <div className="ad-detail-row">
            <span className="ad-detail-label">Billing cycle</span>
            <span className="ad-detail-value">{addon.billingCycle || "—"}</span>
          </div>
          <div className="ad-detail-row">
            <span className="ad-detail-label">Chargeable</span>
            <span className="ad-detail-value">{addon.chargeable ? "Yes" : "No (Free)"}</span>
          </div>
          <div className="ad-detail-row">
            <span className="ad-detail-label">Implementation</span>
            <span className="ad-detail-value">
              {addon.implementationStatus ? "Active" : "Not installed"}
            </span>
          </div>
          <div className="ad-detail-row">
            <span className="ad-detail-label">Status</span>
            <span className="ad-detail-value">{addon.status || "—"}</span>
          </div>
        </div>

      </div>
    </div>
  );
}
